import type { AppState, IngredientList, Operation, UserPreferences } from "@/lib/types";
import { PRIMARY_COLOR } from "@/lib/utils";

// ============================================================================
// Server-side Translation Utility
// ============================================================================

/**
 * Get translation for a specific key and locale (server-side)
 * Used for utilities that need translations without React hooks
 */
function getTranslation(locale: string, namespace: string, key: string): string {
  try {
    // Import messages synchronously - these are bundled at build time
    const messages = require(`../messages/${locale}.json`);
    return messages[namespace]?.[key] || key;
  } catch (error) {
    console.error(`Failed to load translation for ${locale}:${namespace}.${key}`, error);
    return key;
  }
}

// ============================================================================
// Storage Keys - Centralized Definition
// ============================================================================
export const STORAGE_KEY = "nombox-data";
export const PREFERENCES_KEY = "nombox-preferences";
export const TUTORIAL_SEEN_KEY = "nombox-tutorial-seen";
export const PAGE_TUTORIAL_PREFIX = "nombox-tutorial-seen-";
export type PageTutorialId = "dashboard" | "recipes" | "calendar";
export const VOICE_TIP_SEEN_KEY = "nombox-voice-tip-seen";
export const ANALYTICS_QUEUE_KEY = "nombox-analytics-queue";
export const DEVELOPER_OPTIONS_KEY = "nombox-developer-options";
export const BETA_ACCESS_KEY = "nombox-beta-access";
export const SYNC_STATE_KEY = "nombox-sync-state";
export const PENDING_OPERATIONS_KEY = "nombox-pending-operations";
export const SESSION_CACHE_KEY = "nombox-session-cache";

/**
 * Load app state from localStorage
 * Always returns valid state, never null
 */
export function loadState(): AppState {
  if (typeof window === "undefined") {
    return { lists: [], activeListId: null };
  }

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as AppState;

      // Validate parsed data has required structure
      if (parsed && typeof parsed === "object" && Array.isArray(parsed.lists)) {
        // Ensure activeListId is valid or null
        if (parsed.activeListId && !parsed.lists.some((list) => list.id === parsed.activeListId)) {
          parsed.activeListId = parsed.lists.length > 0 ? parsed.lists[0].id : null;
        }
        return parsed;
      }
    }
  } catch (error) {
    console.error("Failed to load state from localStorage:", error);
  }

  // Get user's language preference to localize the default list name
  const preferences = loadPreferences();
  const defaultListName = getTranslation(preferences.language || "en", "lists", "default");

  // Create default state with localized default list name
  const defaultList: IngredientList = {
    id: `${Date.now()}-default`,
    name: defaultListName,
    color: PRIMARY_COLOR,
    ingredients: [],
  };

  const defaultState = {
    lists: [defaultList],
    activeListId: defaultList.id,
  };

  // Save the default state to localStorage
  saveState(defaultState);

  return defaultState;
}

/**
 * Save app state to localStorage
 */
export function saveState(state: AppState): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.error("Failed to save state to localStorage:", error);
  }
}

/**
 * Load user preferences from localStorage
 * Always returns valid preferences, never null
 */
export function loadPreferences(): UserPreferences {
  if (typeof window === "undefined") {
    return { theme: "system", language: "en" };
  }

  try {
    const stored = localStorage.getItem(PREFERENCES_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);

      // Validate parsed data and merge with defaults
      if (parsed && typeof parsed === "object") {
        // Normalize theme: if not light/dark, default to system (backwards compatible with "auto")
        const normalizedTheme =
          parsed.theme === "light" || parsed.theme === "dark" ? parsed.theme : "system";

        return {
          theme: normalizedTheme,
          language: parsed.language || "en",
          ...parsed,
        };
      }
    }
  } catch (error) {
    console.error("Failed to load preferences from localStorage:", error);
  }

  const defaultPreferences: UserPreferences = { theme: "system", language: "en" };

  // Save default preferences to localStorage
  savePreferences(defaultPreferences);

  return defaultPreferences;
}

/**
 * Save user preferences to localStorage
 */
export function savePreferences(preferences: UserPreferences): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    localStorage.setItem(PREFERENCES_KEY, JSON.stringify(preferences));
  } catch (error) {
    console.error("Failed to save preferences to localStorage:", error);
  }
}

/**
 * Clear all app data from localStorage
 * Used when logging in/out to ensure clean state separation
 */
export function clearStorage(): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(SYNC_STATE_KEY); // Also clear sync state on logout
    localStorage.removeItem(PENDING_OPERATIONS_KEY); // Also clear pending operations on logout
    localStorage.removeItem(SESSION_CACHE_KEY); // Also clear session cache on logout
    console.log("🧹 Cleared localStorage, sync state, pending operations, and session cache");
  } catch (error) {
    console.error("Failed to clear localStorage:", error);
  }
}

/**
 * Check if user has seen the tutorial
 */
export function hasTutorialBeenSeen(): boolean {
  if (typeof window === "undefined") {
    return true; // Assume seen on server-side to avoid showing on SSR
  }

  try {
    const seen = localStorage.getItem(TUTORIAL_SEEN_KEY);
    return seen === "true";
  } catch (error) {
    console.error("Failed to check tutorial status:", error);
    return false;
  }
}

/**
 * Mark tutorial as seen
 */
export function markTutorialAsSeen(): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    localStorage.setItem(TUTORIAL_SEEN_KEY, "true");
  } catch (error) {
    console.error("Failed to mark tutorial as seen:", error);
  }
}

export function hasPageTutorialBeenSeen(page: PageTutorialId): boolean {
  if (typeof window === "undefined") return true;
  try {
    return localStorage.getItem(PAGE_TUTORIAL_PREFIX + page) === "true";
  } catch {
    return false;
  }
}

export function markPageTutorialAsSeen(page: PageTutorialId): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(PAGE_TUTORIAL_PREFIX + page, "true");
  } catch {
    /* noop */
  }
}

export function resetPageTutorialSeen(page: PageTutorialId): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(PAGE_TUTORIAL_PREFIX + page);
  } catch {
    /* noop */
  }
}

export const ALL_PAGE_TUTORIAL_IDS: PageTutorialId[] = ["dashboard", "recipes", "calendar"];

/**
 * Check if user has seen the voice tip
 * Returns true on server-side to prevent hydration mismatches and avoid
 * flashing the modal during SSR. The actual check happens client-side.
 */
export function hasVoiceTipBeenSeen(): boolean {
  if (typeof window === "undefined") {
    return true; // Assume seen on server-side to avoid showing on SSR
  }

  try {
    const seen = localStorage.getItem(VOICE_TIP_SEEN_KEY);
    return seen === "true";
  } catch (error) {
    console.error("Failed to check voice tip status:", error);
    return false;
  }
}

/**
 * Mark voice tip as seen
 */
export function markVoiceTipAsSeen(): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    localStorage.setItem(VOICE_TIP_SEEN_KEY, "true");
  } catch (error) {
    console.error("Failed to mark voice tip as seen:", error);
  }
}

/**
 * Analytics Event Queue Types
 */
export interface QueuedAnalyticsEvent {
  id: string;
  eventName: string;
  eventData?: Record<string, any>;
  timestamp: number; // Original timestamp when event was created
  retryCount: number;
  lastAttempt?: number;
}

/**
 * Load analytics queue from localStorage
 */
export function loadAnalyticsQueue(): QueuedAnalyticsEvent[] {
  if (typeof window === "undefined") {
    return [];
  }

  try {
    const stored = localStorage.getItem(ANALYTICS_QUEUE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error("Failed to load analytics queue from localStorage:", error);
  }

  return [];
}

/**
 * Save analytics queue to localStorage
 */
export function saveAnalyticsQueue(queue: QueuedAnalyticsEvent[]): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    localStorage.setItem(ANALYTICS_QUEUE_KEY, JSON.stringify(queue));
  } catch (error) {
    console.error("Failed to save analytics queue to localStorage:", error);
  }
}

/**
 * Clear analytics queue from localStorage
 */
export function clearAnalyticsQueue(): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    localStorage.removeItem(ANALYTICS_QUEUE_KEY);
    console.log("🧹 Cleared analytics queue");
  } catch (error) {
    console.error("Failed to clear analytics queue:", error);
  }
}

// ============================================================================
// Developer Options Storage
// ============================================================================

export type DeveloperOptions = {
  developerMode: boolean;
  version?: number;
  [key: string]: any;
};

const DEVELOPER_OPTIONS_CUSTOM_EVENT = "nombox:developer-options-changed";

function ensureDeveloperOptionsDefaults(obj?: Partial<DeveloperOptions>): DeveloperOptions {
  return {
    developerMode: false,
    version: 1,
    ...(obj || {}),
  };
}

/**
 * Load developer options from localStorage
 * Always returns valid defaults, never null
 */
export function loadDeveloperOptions(): DeveloperOptions {
  if (typeof window === "undefined") {
    return ensureDeveloperOptionsDefaults();
  }

  try {
    const raw = localStorage.getItem(DEVELOPER_OPTIONS_KEY);
    if (!raw) {
      return ensureDeveloperOptionsDefaults();
    }

    const parsed = JSON.parse(raw) as DeveloperOptions;
    return ensureDeveloperOptionsDefaults(parsed);
  } catch (err) {
    console.error("Failed to load developer options:", err);
    return ensureDeveloperOptionsDefaults();
  }
}

/**
 * Save developer options to localStorage
 */
export function saveDeveloperOptions(opts: DeveloperOptions): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    localStorage.setItem(DEVELOPER_OPTIONS_KEY, JSON.stringify(opts));
    // Dispatch custom event for same-tab listeners
    window.dispatchEvent(new CustomEvent(DEVELOPER_OPTIONS_CUSTOM_EVENT, { detail: opts }));
  } catch (err) {
    console.error("Failed to save developer options:", err);
  }
}

/**
 * Clear developer options from localStorage
 */
export function clearDeveloperOptions(): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    localStorage.removeItem(DEVELOPER_OPTIONS_KEY);
    window.dispatchEvent(
      new CustomEvent(DEVELOPER_OPTIONS_CUSTOM_EVENT, {
        detail: ensureDeveloperOptionsDefaults(),
      })
    );
  } catch (err) {
    console.error("Failed to clear developer options:", err);
  }
}

/**
 * Get the custom event name for developer options changes
 */
export function getDeveloperOptionsEventName(): string {
  return DEVELOPER_OPTIONS_CUSTOM_EVENT;
}

// ============================================================================
// Beta Access Storage
// ============================================================================

/**
 * Get stored beta access code from localStorage
 */
export function getStoredBetaCode(): string | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return localStorage.getItem(BETA_ACCESS_KEY);
  } catch (error) {
    console.error("Failed to get beta access code:", error);
    return null;
  }
}

/**
 * Store beta access code in localStorage
 */
export function storeBetaCode(code: string): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    localStorage.setItem(BETA_ACCESS_KEY, code);
  } catch (error) {
    console.error("Failed to store beta access code:", error);
  }
}

// ============================================================================
// Sync State Storage (tracks whether initial server sync has occurred)
// ============================================================================

interface SyncState {
  hasLoadedServerData: boolean;
  userId?: string; // Track which user this sync state belongs to
}

/**
 * Get sync state from localStorage
 */
export function getSyncState(): SyncState {
  if (typeof window === "undefined") {
    return { hasLoadedServerData: false };
  }

  try {
    const stored = localStorage.getItem(SYNC_STATE_KEY);
    if (stored) {
      return JSON.parse(stored) as SyncState;
    }
  } catch (error) {
    console.error("Failed to get sync state:", error);
  }

  return { hasLoadedServerData: false };
}

/**
 * Save sync state to localStorage
 */
export function saveSyncState(state: SyncState): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    localStorage.setItem(SYNC_STATE_KEY, JSON.stringify(state));
  } catch (error) {
    console.error("Failed to save sync state:", error);
  }
}

// ============================================================================
// Pending Operations Storage (operations queued for sync)
// ============================================================================

/**
 * Load pending operations from localStorage
 */
export function loadPendingOperations(): Operation[] {
  if (typeof window === "undefined") {
    return [];
  }

  try {
    const stored = localStorage.getItem(PENDING_OPERATIONS_KEY);
    if (stored) {
      return JSON.parse(stored) as Operation[];
    }
  } catch (error) {
    console.error("Failed to load pending operations from localStorage:", error);
  }

  return [];
}

/**
 * Save pending operations to localStorage
 */
export function savePendingOperations(operations: Operation[]): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    localStorage.setItem(PENDING_OPERATIONS_KEY, JSON.stringify(operations));
  } catch (error) {
    console.error("Failed to save pending operations to localStorage:", error);
  }
}

/**
 * Clear pending operations from localStorage
 */
export function clearPendingOperations(): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    localStorage.removeItem(PENDING_OPERATIONS_KEY);
  } catch (error) {
    console.error("Failed to clear pending operations:", error);
  }
}

// ============================================================================
// Session Cache - Optimistic Session Loading
// ============================================================================

export interface CachedSession {
  user?: {
    id: string;
    email?: string | null;
    name?: string | null;
    image?: string | null;
    provider?: string;
    premiumTier?: string | null;
  };
  expires?: string;
  cachedAt: number; // Timestamp when cached
}

/**
 * Load cached session from localStorage
 * Returns null if no cache exists or cache is invalid
 */
export function loadCachedSession(): CachedSession | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const cached = localStorage.getItem(SESSION_CACHE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached) as CachedSession;

      // Validate cached session has required structure
      if (parsed && typeof parsed === "object" && parsed.cachedAt) {
        // Only use cache if less than 24 hours old
        const MAX_CACHE_AGE = 24 * 60 * 60 * 1000; // 24 hours
        const age = Date.now() - parsed.cachedAt;

        if (age < MAX_CACHE_AGE) {
          return parsed;
        }
      }
    }
  } catch (error) {
    console.error("Failed to load cached session:", error);
  }

  return null;
}

/**
 * Save session to localStorage cache
 * This provides optimistic loading on app launch
 */
export function saveCachedSession(session: CachedSession | null): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    if (session) {
      const cacheData: CachedSession = {
        ...session,
        cachedAt: Date.now(),
      };
      localStorage.setItem(SESSION_CACHE_KEY, JSON.stringify(cacheData));
    } else {
      // Clear cache if session is null (logged out)
      localStorage.removeItem(SESSION_CACHE_KEY);
    }
  } catch (error) {
    console.error("Failed to save session cache:", error);
  }
}

/**
 * Clear session cache from localStorage
 */
export function clearSessionCache(): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    localStorage.removeItem(SESSION_CACHE_KEY);
  } catch (error) {
    console.error("Failed to clear session cache:", error);
  }
}
